import { authService } from './authService';
import { env } from '@/config/env';

/**
 * Generic API response type
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string | { message?: string; code?: string; };
  message?: string;
  errors?: string[];
}

/**
 * Mirrors the backend PaginatedResult<T> envelope returned by paginated list endpoints.
 * pageNumber is 1-based on the wire; UI layers convert to 0-based for MUI DataGrid.
 */
export interface PaginatedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Optional filter set for `HttpClient.fetchUserPage`. All fields are additive:
 * the caller passes only the filters it cares about, and empty / undefined
 * fields simply don't appear in the query string. `| undefined` is spelled
 * explicitly because tsconfig has `exactOptionalPropertyTypes: true`, so
 * callers spreading nullable fields need the property values to accept
 * undefined.
 */
export interface UserPageFilters {
  search?: string | undefined;
  isActive?: boolean | undefined;
  roles?: string[] | undefined;
  serviceProviderIds?: string[] | undefined;
  statuses?: string[] | undefined;
  organizations?: string[] | undefined;
  assignedStateIds?: string[] | undefined;
  includeUnassignedAreas?: boolean | undefined;
}

function appendRepeatable(
  params: URLSearchParams,
  key: string,
  values: readonly string[] | undefined
): void {
  if (!values) return;
  for (const v of values) {
    if (v) params.append(key, v);
  }
}

/**
 * HTTP client error with additional properties
 */
export class HttpClientError extends Error {
  status: number;
  code?: string;
  field?: string;
  responseData?: any;

  constructor(message: string, status: number, code?: string, field?: string, responseData?: any) {
    super(message);
    this.name = 'HttpClientError';
    this.status = status;
    if (code !== undefined) this.code = code;
    if (field !== undefined) this.field = field;
    if (responseData !== undefined) this.responseData = responseData;
  }
}

/**
 * Shared HTTP client with authentication and error handling
 */
export class HttpClient {
  /**
   * Fetch with authentication handling
   * Uses Permission Token (Token #2) from authService when authorization is enabled
   * When authorization is disabled, makes requests without tokens
   */
  static async fetchWithAuth<T = any>(
    url: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const shouldUseAuth = env.authEnabled || env.authorizationEnabled;

    const response = shouldUseAuth
      ? await authService.authenticatedFetch(url, options)
      : await fetch(url, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            ...(options.headers || {}),
          },
        });

    if (!response.ok) {
      throw await this.handleErrorResponse(response);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return { success: true } as ApiResponse<T>;
    }

    // Check if response has JSON content
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      // Return the data directly to maintain backward compatibility
      // Services will handle the response structure
      return data as ApiResponse<T>;
    }

    // For non-JSON responses, return empty object with success flag
    return { success: true } as ApiResponse<T>;
  }

  /**
   * Fetch with simple token-based authentication (for services not using authService)
   */
  static async fetchWithToken<T = any>(
    url: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const shouldUseAuth = env.authEnabled || env.authorizationEnabled;
    const token = shouldUseAuth ? localStorage.getItem('authToken') : null;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      // Handle 401 Unauthorized
      if (response.status === 401 && shouldUseAuth) {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
      }
      
      throw await this.handleErrorResponse(response);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return { success: true } as ApiResponse<T>;
    }

    // Parse JSON response
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      // Return the data directly to maintain backward compatibility
      return data as ApiResponse<T>;
    }

    return { success: true } as ApiResponse<T>;
  }

  /**
   * Build the standard `/Users` list query and return the raw PaginatedResult.
   * Shared by the Internal and Service Provider user services (which differ only
   * by userType and by which token-bearing fetch wrapper they pass in).
   * page is 0-based (MUI DataGrid convention); the server expects 1-based pageNumber.
   */
  static async fetchUserPage(
    fetcher: (endpoint: string, options: RequestInit) => Promise<ApiResponse<PaginatedResult<any>>>,
    userType: 'Internal' | 'External',
    page: number,
    pageSize: number,
    filters: UserPageFilters = {}
  ): Promise<PaginatedResult<any>> {
    const params = new URLSearchParams();
    params.set('userType', userType);
    params.set('pageNumber', String(page + 1));
    params.set('pageSize', String(pageSize));

    const trimmedSearch = filters.search?.trim();
    if (trimmedSearch) {
      params.set('search', trimmedSearch);
    }

    // When isActive is explicitly set, send it to the API.
    // Omitting it (undefined) means the API returns all users regardless of status.
    if (filters.isActive !== undefined) {
      params.set('isActive', String(filters.isActive));
    }

    // Repeatable list params — .append (not .set) so the backend receives
    // ?roles=Driver&roles=Monitor etc. ASP.NET Core binds repeated keys to List<T>.
    appendRepeatable(params, 'roles', filters.roles);
    appendRepeatable(params, 'serviceProviderIds', filters.serviceProviderIds);
    appendRepeatable(params, 'statuses', filters.statuses);
    appendRepeatable(params, 'organizations', filters.organizations);
    appendRepeatable(params, 'assignedStateIds', filters.assignedStateIds);
    if (filters.includeUnassignedAreas) {
      params.set('includeUnassignedAreas', 'true');
    }

    // The fetch wrapper returns the raw parsed JSON typed as ApiResponse<T>;
    // the server actually sends a PaginatedResult<UserDto>, so read it as such.
    const response = await fetcher(`/Users?${params.toString()}`, { method: 'GET' });
    return response as unknown as PaginatedResult<any>;
  }

  /**
   * Extract error message from API response
   */
  private static async handleErrorResponse(response: Response): Promise<HttpClientError> {
    let errorData: any;
    
    try {
      errorData = await response.json();
    } catch {
      errorData = null;
    }

    // Prefer the per-field validation reason over the generic
    // top-level "Validation failed" message. GlobalExceptionMiddleware sends
    // { message: "Validation failed", errors: { FieldName: ["specific reason"] } } -
    // errorData.message is always truthy on a validation failure, so it must be
    // checked AFTER the field errors or the specific reason is never surfaced.
    // `errors` is a Dictionary<string,string[]>, not an array, so it must be
    // flattened rather than indexed with `errors?.[0]`.
    const fieldErrors: string[] = errorData?.errors
      ? Object.values(errorData.errors).flat().filter((m): m is string => typeof m === 'string')
      : [];

    // Extract error message from various response formats
    const errorMessage =
      errorData?.error?.message ||
      fieldErrors[0] ||
      errorData?.message ||
      (typeof errorData?.error === 'string' ? errorData.error : null) ||
      (typeof errorData === 'string' ? errorData : null) ||
      `HTTP error! status: ${response.status}`;

    const code = 
      errorData?.error?.code || 
      errorData?.code || 
      (response.status === 409 ? 'CONFLICT' : 'ERROR');

    const field = errorData?.error?.field;

    return new HttpClientError(errorMessage, response.status, code, field, errorData);
  }

  /**
   * Maps area items (states, districts, dispatch areas) from API response
   * Handles both object format {id, name} and string GUID format
   */
  static mapAreaItem(item: any): { id: string; name: string } {
    // If already an object with id and name, use it
    if (typeof item === 'object' && item) {
      const id = item.id || item.stateId || item.districtId || item.dispatchAreaId || item.Id;
      const name = item.name || item.Name || item.stateName || item.StateName ||
                  item.dispatchAreaName || item.DispatchAreaName || item.areaName || item.AreaName ||
                  item.districtName || item.DistrictName || id; // Use ID as fallback name
      return {
        id: id || String(item),
        name: name || String(item)
      };
    }
    // If it's a GUID string, return it as both id and name (UI layer will map it)
    if (typeof item === 'string') {
      return {
        id: item,
        name: item // UI layer will replace with actual name
      };
    }
    
    // Fallback for unexpected types
    return {
      id: String(item),
      name: String(item)
    };
  }
}
